// Web-build authentication helpers. The desktop (Tauri) build never calls these.

export type AuthMode = "none" | "db_auth" | "mfa_auth" | "ad_auth";

export async function fetchAuthMode(): Promise<AuthMode> {
  try {
    const r = await fetch("/api/auth/config");
    const j = (await r.json()) as { mode: AuthMode };
    return j.mode ?? "none";
  } catch {
    return "none";
  }
}

/** True when a valid session exists (or auth is disabled). */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const r = await fetch("/api/auth/me");
    return r.ok;
  } catch {
    return false;
  }
}

export type LoginOutcome =
  | { kind: "ok" }
  | { kind: "mfa"; challenge: string }
  | { kind: "error"; message: string };

/** Submit a db_auth/mfa_auth password login. */
export async function login(username: string, password: string): Promise<LoginOutcome> {
  try {
    const r = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const j = (await r.json().catch(() => ({}))) as {
      status?: string;
      challenge?: string;
      error?: string;
    };
    if (r.ok) {
      if (j.status === "mfa_required" && j.challenge) {
        return { kind: "mfa", challenge: j.challenge };
      }
      return { kind: "ok" };
    }
    return { kind: "error", message: j.error ?? "Login failed." };
  } catch {
    return { kind: "error", message: "Could not reach the server." };
  }
}

/** Submit a TOTP code for an MFA challenge. Returns null on success, else an error.
 *  `remember` asks the server to mark this browser trusted, skipping the code
 *  prompt on later logins until the trust window lapses. */
export async function verifyTotp(
  challenge: string,
  code: string,
  remember = true,
): Promise<string | null> {
  try {
    const r = await fetch("/api/auth/verify-totp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challenge, code, remember }),
    });
    if (r.ok) return null;
    const j = await r.json().catch(() => ({}));
    return (j as { error?: string }).error ?? "Verification failed.";
  } catch {
    return "Could not reach the server.";
  }
}

/** Submit an Active Directory login. Returns null on success, else an error. */
export async function adLogin(username: string, password: string): Promise<string | null> {
  try {
    const r = await fetch("/api/auth/ad-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (r.ok) return null;
    const j = await r.json().catch(() => ({}));
    return (j as { error?: string }).error ?? "Login failed.";
  } catch {
    return "Could not reach the server.";
  }
}

/**
 * Ask for a password-reset link. Always resolves successfully when the server is
 * reachable: the backend deliberately does not reveal whether the account exists.
 */
export async function requestPasswordReset(username: string): Promise<string | null> {
  try {
    const r = await fetch("/api/auth/forgot-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    if (r.ok) return null;
    const j = await r.json().catch(() => ({}));
    return (j as { error?: string }).error ?? "Could not request a password reset.";
  } catch {
    return "Could not reach the server.";
  }
}

/** Errors from a password write: `errors` lists every failed complexity rule. */
function passwordErrors(j: unknown, fallback: string): string[] {
  const o = (j ?? {}) as { errors?: string[]; error?: string };
  if (o.errors?.length) return o.errors;
  return [o.error ?? fallback];
}

/** Consume a reset token and set a new password. Returns [] on success. */
export async function submitPasswordReset(
  token: string,
  password: string,
  confirm: string,
): Promise<string[]> {
  try {
    const r = await fetch("/api/auth/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password, confirm }),
    });
    if (r.ok) return [];
    return passwordErrors(await r.json().catch(() => ({})), "Could not reset the password.");
  } catch {
    return ["Could not reach the server."];
  }
}

/** Change the signed-in user's password. Returns [] on success. */
export async function changePassword(
  oldPassword: string,
  newPassword: string,
  confirm: string,
): Promise<string[]> {
  try {
    const r = await fetch("/api/auth/change-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
        confirm,
      }),
    });
    if (r.ok) return [];
    return passwordErrors(await r.json().catch(() => ({})), "Could not change the password.");
  } catch {
    return ["Could not reach the server."];
  }
}

/** A minted Excel data token, valid on `site` only; `expires` is in Unix seconds. */
export interface DataToken {
  token: string;
  username: string;
  site: string;
  expires: number;
}

/**
 * Mint a data token for the signed-in user (Excel Power Query's Basic-auth
 * password for `/api/data/*`). Resolves to the token, or to an error message.
 */
export async function createDataToken(): Promise<DataToken | { error: string }> {
  try {
    const r = await fetch("/api/auth/data-token", { method: "POST" });
    const body = await r.json().catch(() => ({}));
    if (r.ok && typeof body.token === "string") return body as DataToken;
    return { error: typeof body.error === "string" ? body.error : "" };
  } catch {
    return { error: "Could not reach the server." };
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch("/api/auth/logout", { method: "POST" });
  } catch {
    /* ignore */
  }
}
